import {
  PUBLIC_PROFILE_XP_EVENT_LEDGER_VERSION
} from "./publicProfileStats";
import { normalizeExperienceEvent } from "./experienceNotification";
import { applyExperienceValueChanges } from "./experienceValueLedger";
import { requestExperienceSettlement } from "./experienceSettlementApi";
import type { ExperienceComponentValue, ExperienceEventInput, ExperienceMutation, ExperienceMutationComponent } from "../types/experience";
import type { DateValue } from "../types/data";
import { isUnknownArray, isRecord } from "../types/data";
import type { PublicStats } from "../types/profile";
import type { Firestore } from "firebase/firestore";
import type firebase from "firebase/compat/app";

export const EXPERIENCE_MUTATION_OUTBOX_VERSION = 1;
export const EXPERIENCE_MUTATION_PENDING = "pending";
export const EXPERIENCE_MUTATION_APPLIED = "applied";

const MAX_EXPERIENCE_DELTA = 1_000_000_000;
const MAX_COMPONENTS = 256;
const RECENT_MUTATION_LIMIT = 32;

interface ExperiencePublicStats extends PublicStats {
  activeExperienceComponentIds?: string[];
  removedExperienceComponentIds?: string[];
  recentExperienceComponentMutationIds?: string[];
  recentExperienceMutationIds?: string[];
  experienceComponentValues?: ExperienceComponentValue[];
  xpEventLedgerVersion?: number;
}

const normalizeIds = (value: unknown, limit = RECENT_MUTATION_LIMIT) => [
  ...new Set(
    (isUnknownArray(value) ? value : [])
      .map((item) => String(item || "").trim())
      .filter(Boolean)
  )
].slice(-limit);

const normalizeDelta = (value: unknown) => {
  const delta = Number(value);
  return Number.isFinite(delta) && Math.abs(delta) <= MAX_EXPERIENCE_DELTA
    ? delta
    : null;
};

const normalizeComponents = (value: unknown): ExperienceMutationComponent[] | null => {
  if (!isUnknownArray(value) || value.length > MAX_COMPONENTS) return null;
  const seen = new Set<string>();
  const components: ExperienceMutationComponent[] = [];

  for (const item of value) {
    if (!isRecord(item)) return null;
    const id = String(item?.id || "").trim();
    const delta = normalizeDelta(item?.delta);
    if (!id || id.length > 500 || delta === null || delta === 0 || seen.has(id)) {
      return null;
    }
    seen.add(id);
    components.push({ id, delta });
  }

  return components;
};

export const normalizeExperienceMutation = (value: unknown): ExperienceMutation | null => {
  if (!isRecord(value)) return null;
  const version = Number(value.version);
  const id = String(value.id || "").trim();
  const status = String(value.status || "").trim();
  const directDelta = normalizeDelta(value.directDelta);
  const components = normalizeComponents(value.components);
  const event = value.event == null ? null : normalizeExperienceEvent(value.event);

  if (
    version !== EXPERIENCE_MUTATION_OUTBOX_VERSION ||
    !id ||
    id.length > 300 ||
    (status !== EXPERIENCE_MUTATION_PENDING && status !== EXPERIENCE_MUTATION_APPLIED) ||
    directDelta === null ||
    components === null ||
    (value.event != null && event === null)
  ) {
    return null;
  }

  return {
    version,
    id,
    status,
    directDelta,
    components,
    ...(event ? { event } : {}),
    ...(value.createdAt != null ? { createdAt: value.createdAt as DateValue } : {}),
    ...(value.appliedAt != null ? { appliedAt: value.appliedAt as DateValue } : {})
  };
};

export const createPendingExperienceMutation = ({
  id,
  directDelta,
  components = [],
  event = null,
  createdAt = null
}: { id?: string; directDelta?: number; components?: ExperienceMutationComponent[]; event?: ExperienceEventInput | null; createdAt?: DateValue } = {}) => {
  const mutation = normalizeExperienceMutation({
    version: EXPERIENCE_MUTATION_OUTBOX_VERSION,
    id,
    status: EXPERIENCE_MUTATION_PENDING,
    directDelta,
    components,
    ...(event ? { event } : {}),
    ...(createdAt != null ? { createdAt } : {})
  });
  if (!mutation) throw new TypeError("Invalid experience mutation outbox payload");
  return mutation;
};

const appendRecentId = (ids: string[], id: string) => [
  ...ids.filter((item) => item !== id),
  id
].slice(-RECENT_MUTATION_LIMIT);

// Used only while a missing aggregate is initialized from already-updated
// rating/progress sources. In that case the fresh total already includes the
// domain mutation, so we record its idempotency markers and close the outbox
// without applying any XP delta a second time. The caller must persist both
// returned objects in the same transaction as the initialized total.
export const absorbPendingExperienceMutation = ({
  publicStats = {},
  mutation: value,
  appliedAt = null
}: { publicStats?: ExperiencePublicStats; mutation?: ExperienceMutation | null; appliedAt?: DateValue } = {}) => {
  const mutation = normalizeExperienceMutation(value);
  if (!mutation || mutation.status !== EXPERIENCE_MUTATION_PENDING) return null;

  const activeIds = new Set(normalizeIds(
    publicStats.activeExperienceComponentIds,
    2000
  ));
  const removedIds = new Set(normalizeIds(
    publicStats.removedExperienceComponentIds,
    2000
  ));
  let recentComponentMutationIds = normalizeIds(
    publicStats.recentExperienceComponentMutationIds,
    RECENT_MUTATION_LIMIT
  );

  const absorbedValueChanges = mutation.components.map((component) => {
    const action = component.delta > 0 ? "add" : "remove";
    const oppositeAction = action === "add" ? "remove" : "add";
    recentComponentMutationIds = appendRecentId(
      recentComponentMutationIds.filter(
        (id) => id !== `${oppositeAction}:${component.id}`
      ),
      `${action}:${component.id}`
    );
    return {
      id: component.id,
      targetExperience: action === "add" ? Math.abs(component.delta) : 0,
      previousExperience: Math.abs(component.delta),
      // The initialized aggregate already contains the mutation. This seeds
      // the amount/state ledger while deliberately discarding its delta.
      usePreviousWhenMissing: true
    };
  });
  const absorbedValueResult = applyExperienceValueChanges({
    storedValues: publicStats.experienceComponentValues,
    activeIds,
    removedIds,
    changes: absorbedValueChanges
  });

  const recentMutationIds = normalizeIds(
    publicStats.recentExperienceMutationIds,
    RECENT_MUTATION_LIMIT
  );
  const nextPublicStats = {
    ...publicStats,
    xpEventLedgerVersion: PUBLIC_PROFILE_XP_EVENT_LEDGER_VERSION,
    recentExperienceMutationIds: mutation.directDelta !== 0
      ? appendRecentId(recentMutationIds, mutation.id)
      : recentMutationIds,
    recentExperienceComponentMutationIds: recentComponentMutationIds,
    activeExperienceComponentIds: [...absorbedValueResult.activeIds].sort(),
    removedExperienceComponentIds: [
      ...absorbedValueResult.removedIds
    ].slice(-2000).sort(),
    experienceComponentValues: absorbedValueResult.values
  };
  const appliedMutation: ExperienceMutation = {
    ...mutation,
    status: EXPERIENCE_MUTATION_APPLIED,
    ...(appliedAt != null ? { appliedAt } : {})
  };

  return {
    publicStats: nextPublicStats,
    mutation: appliedMutation,
    experienceDelta: 0,
    event: null
  };
};

export const applyPendingExperienceMutation = async ({
  db,
  userId,
  filmId,
  expectedMutationId = ""
}: { db?: Firestore | firebase.firestore.Firestore | null; userId?: string; filmId?: string | number; expectedMutationId?: string } = {}) => {
  const normalizedUserId = String(userId || "").trim();
  const normalizedFilmId = String(filmId || "").trim();
  if (!db || !normalizedUserId || !normalizedFilmId) {
    return { applied: false, reason: "invalid-target" };
  }
  return requestExperienceSettlement({
    db,
    userId: normalizedUserId,
    reason: expectedMutationId
      ? `rating:${String(expectedMutationId).slice(0, 60)}`
      : `rating:${normalizedFilmId}`
  });
};
